import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition, faTrash, faPen, faBox, faPallet, faMotorcycle, faHandHolding, faUserCheck, faCalendarDay } from '@fortawesome/free-solid-svg-icons';
import { ShippingConfigService } from '../../../_services/shipping-config.service';
import {
  ShipmentMethod,
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigFormData,
} from '../../../_models/shipping-config.interface';
import { ShippingConfigFormComponent } from '../../_shared/shipping-config-form/shipping-config-form.component';

/**
 * Ticket-Seiten-Wrapper um die Versandkonfiguration einer Operation.
 *
 * Zeigt das Badge mit aktueller Konfig + Edit/Delete-Buttons an, oeffnet auf
 * Klick ein Popup mit der geteilten `ShippingConfigFormComponent` und
 * persistiert beim Speichern ueber die Single-Op-API.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, ShippingConfigFormComponent],
  selector: 'hawk-operation-shipping-config',
  templateUrl: './operation-shipping-config.component.html',
})
export class OperationShippingConfigComponent implements OnInit, OnChanges {
  @Input() operationId!: number;
  @Input() ticketStoreId?: number;
  @Input() projectId?: number;
  @Input() ticketLat?: number;
  @Input() ticketLng?: number;
  @Input() referenceDate?: string;
  @Input() operationTechnicianId?: number;
  /** Optionaler Heading fuer das Form-Popup. */
  @Input() formHeading: string | null = 'Versandkonfiguration';
  /** Optionales Label fuer den "leer"-Zustand und den Erstellen-Button. */
  @Input() emptyLabel = 'Standard (Parsing)';
  // Wird vom Parent jedes Mal hochgezaehlt, wenn die Config sich serverseitig
  // geaendert haben koennte (z.B. nach Auto-Create durch Versand-Req-Anlage).
  @Input() refreshKey = 0;
  @Input() createButtonLabel = 'Versandkonfiguration setzen';

  /** Emittiert die aktuelle Config bei jeder Aenderung — vom Parent z.B. genutzt,
   * um den Versand-Status (shipped_at) fuer abhaengige UI-Bloecke zu kennen. */
  @Output() configChanged = new EventEmitter<ShippingConfig | null>();

  iconDelete = faTrash;
  iconEdit = faPen;
  iconTechnician = faUserCheck;
  iconDate = faCalendarDay;

  config = signal<ShippingConfig | null>(null);
  context = signal<ShippingConfigContext | null>(null);
  showPopup = signal(false);

  badgeClass = computed(() => {
    switch (this.config()?.shipping_type) {
      case 'pickpoint': return 'bm-badge-info';
      case 'store_shipping': return 'bm-badge-warning';
      case 'technician_shipping': return 'bm-badge-success';
      case 'customer_shipping': return 'bm-badge-neutral';
      default: return '';
    }
  });

  /** Icon zur aktuellen Versandart (Paket/Palette/Kurier/Abholung). */
  shipmentMethodIcon = computed<IconDefinition | null>(() => {
    switch (this.config()?.shipment_method) {
      case 'parcel':   return faBox;
      case 'pallet':   return faPallet;
      case 'courier':  return faMotorcycle;
      case 'pickup':   return faHandHolding;
      default:         return null;
    }
  });

  shipmentMethodLabel = computed<string>(() => {
    const map: Record<ShipmentMethod, string> = {
      parcel: 'Paket', pallet: 'Palette', courier: 'Kurier', pickup: 'Abholung',
    };
    const m = this.config()?.shipment_method;
    return m ? map[m] : '';
  });

  /** Bei Paketversand wird die Geschwindigkeit als Suffix angezeigt (" Express" / "" fuer Standard). */
  packageSpeedSuffix = computed<string>(() => {
    const c = this.config();
    if (c?.shipment_method !== 'parcel') return '';
    return c?.package_speed === 'express' ? ' Express' : '';
  });

  constructor(private shippingConfigService: ShippingConfigService) {}

  ngOnInit(): void {
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Erstes Setzen ueberspringen — initialer Load passiert in ngOnInit.
    if (changes['refreshKey'] && !changes['refreshKey'].firstChange) {
      this.loadConfig();
    }
  }

  async openPopup(): Promise<void> {
    // Kontext immer frisch laden: Rueckadresse oder Filialname kann sich geaendert haben.
    this.context.set(null);
    this.showPopup.set(true);
    try {
      const ctx = await this.shippingConfigService.getContext(this.operationId);
      this.context.set(ctx);
    } catch (e) {
      console.error('Kontext konnte nicht geladen werden', e);
      this.context.set({ customer_has_return_address: false });
    }
  }

  closePopup(): void {
    this.showPopup.set(false);
  }

  async onSave(data: ShippingConfigFormData): Promise<void> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await this.shippingConfigService.createOrUpdateShippingConfig(this.operationId, data as any);
      this.config.set(result);
      this.configChanged.emit(result);
      this.closePopup();
    } catch (e) {
      console.error('Fehler beim Speichern der Versandkonfiguration', e);
    }
  }

  private async loadConfig(): Promise<void> {
    const config = await this.shippingConfigService.getShippingConfig(this.operationId);
    this.config.set(config);
    this.configChanged.emit(config);
  }

  async deleteConfig(): Promise<void> {
    try {
      await this.shippingConfigService.deleteShippingConfig(this.operationId);
      this.config.set(null);
      this.configChanged.emit(null);
    } catch (e) {
      console.error('Fehler beim Loeschen der Versandkonfiguration', e);
    }
  }
}
